import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PaymentEmailComposer from "./PaymentEmailComposer";
import { usePaymentsData } from "../widgets/PaymentsDataContext";

const PaymentEmailWorkspaceContext = createContext({ openPaymentEmail: null, isPaymentEmailOpen: false, paymentSubmissionMode: "email" });
export const usePaymentEmailWorkspace = () => useContext(PaymentEmailWorkspaceContext);

export default function PaymentEmailWorkspaceProvider({ caseId, children }) {
  const history = useHistory();
  const location = useLocation();
  const { reloadRequests, reloadCommunications, selectRequest, paymentSubmissionMode = "email" } = usePaymentsData();
  const [sessions, setSessions] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const sequence = useRef(0);
  const handledQuery = useRef(null);
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const openPaymentEmail = useCallback(({ interventionId = null, packetId = null, fromIntervention = false } = {}) => {
    const existing = sessionsRef.current.find(session => !session.sent && (packetId
      ? String(session.packetId || session.savedPacketId) === String(packetId)
      : !session.packetId && String(session.interventionId || "") === String(interventionId || "")));
    const key = existing?.key || `${caseId}:${++sequence.current}`;
    const backLabel = fromIntervention ? "Back to intervention" : "Back to client file";
    setSessions(current => existing ? current.map(session => session.key === key ? { ...session, backLabel } : session) : [...current, { key, interventionId, packetId, backLabel }]);
    setActiveKey(key);
  }, [caseId]);
  useEffect(() => { setSessions([]); setActiveKey(null); handledQuery.current = null; }, [caseId]);
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const interventionId = query.get("preparePaymentIntervention");
    const packetId = query.get("paymentEmailPacket");
    if ((!interventionId && !packetId) || handledQuery.current === location.search) return;
    handledQuery.current = location.search;
    openPaymentEmail({ interventionId, packetId });
    query.delete("preparePaymentIntervention"); query.delete("paymentEmailPacket");
    history.replace({ ...location, search: query.toString() ? `?${query}` : "" });
  }, [history, location, openPaymentEmail]);
  const saved = async (key, result) => {
    setSessions(current => current.map(session => session.key === key ? { ...session, savedPacketId: result.packetId || session.savedPacketId, sent: result.status === "submitted" || result.status === "confirmed" } : session));
    await Promise.all([reloadRequests(), reloadCommunications()]);
    if (result.packetId) selectRequest(String(result.packetId));
  };
  const context = useMemo(() => ({ openPaymentEmail, isPaymentEmailOpen: activeKey !== null, paymentSubmissionMode }), [activeKey, openPaymentEmail, paymentSubmissionMode]);
  return <PaymentEmailWorkspaceContext.Provider value={context}>
    {children}
    {sessions.map(session => <PaymentEmailComposer showActivity onActivityUpdated={reloadRequests} key={session.key} paymentSubmissionMode={paymentSubmissionMode} caseId={caseId} interventionId={session.interventionId} packetId={session.packetId} visible={session.key === activeKey} backLabel={session.backLabel} onBack={() => setActiveKey(null)} onDismiss={() => setActiveKey(null)} onSaved={result => saved(session.key, result)} />)}
  </PaymentEmailWorkspaceContext.Provider>;
}
